import * as React from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils';
import { Eyebrow } from './Eyebrow';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* Bounded rest-prop allow-list (codex Card r1 lesson). */
type AllowedRestKey = 'id' | 'title' | `data-${string}` | `aria-${string}` | 'lang' | 'dir';
type AllowedRest = Partial<Record<AllowedRestKey, unknown>>;
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface StatCellProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  label?: ReactNode;
  value?: ReactNode;
  mono?: boolean;
  span?: number;
}

export const StatCell = React.forwardRef<HTMLDivElement, StatCellProps>(function StatCell(
  { label, value, mono, span = 1, className, unsafe_style, ...rest },
  ref,
) {
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.HTMLAttributes<HTMLDivElement>;

  return (
    <div
      ref={ref}
      {...restProps}
      className={cx('j-stat-cell', className)}
      style={{ gridColumn: `span ${span}`, ...unsafe_style }}
    >
      {/* `label` is renderable when it's a real value — explicitly
          exclude null/undefined (absent), '' (intentionally blank),
          and `false` (the common React conditional pattern
          `label={cond && 'X'}` lands here when cond is false). The
          literal `0` IS a renderable label (a stat where the label
          is the digit zero) and must pass. Avoids both the original
          `label &&` bug (literal 0 rendered outside Eyebrow) and the
          round-1 preemptive-fix regression (empty Eyebrow for false
          labels) — codex round 2. */}
      {label !== null && label !== undefined && label !== false && label !== '' && (
        <Eyebrow>{label}</Eyebrow>
      )}
      <div className={cx('j-stat-cell-value', mono && 'j-stat-cell-value-mono')}>
        {value ?? '—'}
      </div>
    </div>
  );
});
